import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { updatePlay, uploadPlayImage } from '../../../services/playsService';

const PlayListingCard = ({ play, onEdit, onDelete, viewMode = 'box' }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(play?.title || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isTogglingPublish, setIsTogglingPublish] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = React.useRef(null);

  const getCompletionColor = (percentage) => {
    if (percentage >= 80) return 'text-success';
    if (percentage >= 50) return 'text-warning';
    return 'text-error';
  };

  const handleStartEdit = () => {
    setIsEditingTitle(true);
    setEditedTitle(play?.title || '');
    setError(null);
  };

  const handleCancelEdit = () => {
    setIsEditingTitle(false);
    setEditedTitle(play?.title || '');
    setError(null);
  };

  const handleSaveTitle = async () => {
    if (!editedTitle?.trim()) {
      setError('Title cannot be empty');
      return;
    }

    if (editedTitle?.trim() === play?.title) {
      setIsEditingTitle(false);
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      await updatePlay(play?.id, { title: editedTitle?.trim() }, user?.id);

      // Update parent component's play data
      if (onEdit) {
        onEdit({ ...play, title: editedTitle?.trim() });
      }

      setIsEditingTitle(false);
    } catch (err) {
      console.error('Error updating play title:', err);
      setError('Failed to update title. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTogglePublish = async () => {
    try {
      setIsTogglingPublish(true);
      setError(null);

      const newPublishStatus = !play?.isPublished;
      await updatePlay(play?.id, { is_published: newPublishStatus }, user?.id);

      // Update parent component's play data
      if (onEdit) {
        onEdit({ ...play, isPublished: newPublishStatus });
      }
    } catch (err) {
      console.error('Error toggling publish status:', err);
      setError('Failed to update publish status. Please try again.');
    } finally {
      setIsTogglingPublish(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes?.includes(file?.type)) {
      setError('Please upload a valid image file (JPEG, PNG, or WebP)');
      return;
    }

    // Validate file size (10MB max)
    if (file?.size > 10485760) {
      setError('Image size must be less than 10MB');
      return;
    }

    try {
      setIsUploadingImage(true);
      setError(null);

      const imageUrl = await uploadPlayImage(play?.id, file);

      // Update parent component's play data
      if (onEdit) {
        onEdit({ ...play, coverImageUrl: imageUrl });
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err?.message || 'Failed to upload image. Please try again.');
    } finally {
      setIsUploadingImage(false);
      if (fileInputRef?.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImageButtonClick = () => {
    fileInputRef?.current?.click();
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter') {
      handleSaveTitle();
    } else if (e?.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleLineEditing = () => {
    navigate('/edit-play', { state: { playId: play?.id, playTitle: play?.title } });
  };

  // Line view mode
  if (viewMode === 'line') {
    return (
      <div className="bg-card rounded-lg border border-border p-4 hover:shadow-md transition-all duration-200">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 flex items-center gap-4">
            {!isEditingTitle ? (
              <>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-card-foreground">{play?.title}</h3>
                  <p className="text-xs text-muted-foreground">{play?.author}</p>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>{play?.actCount || 0} Acts</span>
                  <span>{play?.sceneCount || 0} Scenes</span>
                  <span>{play?.lineCount || 0} Lines</span>
                  <span>{play?.characterCount || 0} Characters</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Audio:</span>
                  <span className={`text-sm font-medium ${getCompletionColor(play?.audioCompletion || 0)}`}>
                    {play?.audioCompletion || 0}%
                  </span>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center gap-2">
                <Input
                  type="text"
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e?.target?.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter play title"
                  className="flex-1"
                  autoFocus
                  disabled={isSaving}
                />
                <button
                  onClick={handleSaveTitle}
                  disabled={isSaving}
                  className="p-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Save title"
                >
                  <Icon name="Check" size={18} />
                </button>
                <button
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                  className="p-2 rounded-md hover:bg-muted transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Cancel edit"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>
            )}
          </div>
          {!isEditingTitle && (
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageUpload}
                className="hidden"
              />
              <button
                onClick={handleImageButtonClick}
                disabled={isUploadingImage}
                className="p-2 rounded-md hover:bg-muted transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Upload cover image"
                title="Upload cover image (16:9 landscape ratio recommended)"
              >
                <Icon name={isUploadingImage ? "Loader2" : "Image"} size={18} className={`text-muted-foreground ${isUploadingImage ? 'animate-spin' : ''}`} />
              </button>
              <Button
                variant={play?.isPublished ? 'outline' : 'default'}
                size="sm"
                onClick={handleTogglePublish}
                disabled={isTogglingPublish}
                iconName={play?.isPublished ? 'EyeOff' : 'Eye'}
                iconPosition="left"
              >
                {play?.isPublished ? 'Unpublish' : 'Publish'}
              </Button>
              <button
                onClick={handleLineEditing}
                className="p-2 rounded-md hover:bg-muted transition-colors duration-150"
                aria-label="Edit lines"
              >
                <Icon name="Edit3" size={18} className="text-muted-foreground" />
              </button>
              <button
                onClick={handleStartEdit}
                className="p-2 rounded-md hover:bg-muted transition-colors duration-150"
                aria-label="Edit play title"
              >
                <Icon name="Edit" size={18} className="text-muted-foreground" />
              </button>
              <button
                onClick={() => onDelete(play)}
                className="p-2 rounded-md hover:bg-error/10 transition-colors duration-150"
                aria-label="Delete play"
              >
                <Icon name="Trash2" size={18} className="text-error" />
              </button>
            </div>
          )}
        </div>
        {error && (
          <p className="text-xs text-destructive mt-2">{error}</p>
        )}
      </div>
    );
  }

  // Box view mode (default)
  return (
    <div className="bg-card rounded-lg border border-border p-6 hover:shadow-lg transition-all duration-200">
      {/* Cover Image Section */}
      <div className="mb-4 relative group">
        {play?.coverImageUrl ? (
          <div className="aspect-[16/9] w-full rounded-lg overflow-hidden relative">
            <img
              src={play?.coverImageUrl}
              alt={`${play?.title} cover`}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageUpload}
                className="hidden"
              />
              <button
                onClick={handleImageButtonClick}
                disabled={isUploadingImage}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Icon name={isUploadingImage ? "Loader2" : "Upload"} size={18} className={isUploadingImage ? 'animate-spin' : ''} />
                {isUploadingImage ? 'Uploading...' : 'Change Image'}
              </button>
            </div>
          </div>
        ) : (
          <div className="aspect-[16/9] w-full rounded-lg border-2 border-dashed border-border bg-muted/30 flex flex-col items-center justify-center gap-3 hover:border-primary/50 transition-colors duration-200">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleImageUpload}
              className="hidden"
            />
            <Icon name="ImagePlus" size={48} className="text-muted-foreground" />
            <div className="text-center px-4">
              <p className="text-sm text-muted-foreground mb-1">No cover image</p>
              <p className="text-xs text-muted-foreground/70 mb-3">Ideal ratio: 16:9 (landscape)</p>
              <button
                onClick={handleImageButtonClick}
                disabled={isUploadingImage}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center gap-2 mx-auto"
              >
                <Icon name={isUploadingImage ? "Loader2" : "Upload"} size={16} className={isUploadingImage ? 'animate-spin' : ''} />
                {isUploadingImage ? 'Uploading...' : 'Upload Image'}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          {isEditingTitle ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e?.target?.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter play title"
                  className="flex-1"
                  autoFocus
                  disabled={isSaving}
                />
                <button
                  onClick={handleSaveTitle}
                  disabled={isSaving}
                  className="p-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Save title"
                >
                  <Icon name="Check" size={18} />
                </button>
                <button
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                  className="p-2 rounded-md hover:bg-muted transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Cancel edit"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>
              {error && (
                <p className="text-xs text-destructive">{error}</p>
              )}
            </div>
          ) : (
            <>
              <h3 className="text-lg font-semibold text-card-foreground mb-1">{play?.title}</h3>
              <p className="text-sm text-muted-foreground">{play?.author}</p>
            </>
          )}
        </div>
        {!isEditingTitle && (
          <div className="flex items-center gap-2">
            <Button
              variant={play?.isPublished ? 'outline' : 'default'}
              size="sm"
              onClick={handleTogglePublish}
              disabled={isTogglingPublish}
              iconName={play?.isPublished ? 'EyeOff' : 'Eye'}
              iconPosition="left"
            >
              {play?.isPublished ? 'Unpublish' : 'Publish'}
            </Button>
            <button
              onClick={handleStartEdit}
              className="p-2 rounded-md hover:bg-muted transition-colors duration-150"
              aria-label="Edit play title"
            >
              <Icon name="Edit" size={18} className="text-muted-foreground" />
            </button>
            <button
              onClick={() => onDelete(play)}
              className="p-2 rounded-md hover:bg-error/10 transition-colors duration-150"
              aria-label="Delete play"
            >
              <Icon name="Trash2" size={18} className="text-error" />
            </button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div className="bg-muted rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="BookOpen" size={16} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Acts</span>
          </div>
          <p className="text-xl font-semibold text-card-foreground">{play?.actCount || 0}</p>
        </div>

        <div className="bg-muted rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="FileText" size={16} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Scenes</span>
          </div>
          <p className="text-xl font-semibold text-card-foreground">{play?.sceneCount || 0}</p>
        </div>

        <div className="bg-muted rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="MessageSquare" size={16} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Lines</span>
          </div>
          <p className="text-xl font-semibold text-card-foreground">{play?.lineCount || 0}</p>
        </div>

        <div className="bg-muted rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="Users2" size={16} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Characters</span>
          </div>
          <p className="text-xl font-semibold text-card-foreground">{play?.characterCount || 0}</p>
        </div>
      </div>

      <div className="mb-4 bg-muted rounded-lg p-3">
        <div className="flex items-center gap-2 mb-1">
          <Icon name="GraduationCap" size={16} className="text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Active Students Learning</span>
        </div>
        <p className="text-xl font-semibold text-card-foreground">{play?.studentCount || 0}</p>
      </div>

      <div className="space-y-3 mb-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Audio Files</span>
            <span className={`text-sm font-medium ${getCompletionColor(play?.audioCompletion || 0)}`}>
              {play?.audioCompletion || 0}%
            </span>
          </div>
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${play?.audioCompletion || 0}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {play?.audioFilesUploaded || 0} of {play?.lineCount || 0} lines have audio
          </p>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Glossary Terms</span>
            <span className={`text-sm font-medium ${getCompletionColor(play?.glossaryCompletion || 0)}`}>
              {play?.glossaryCompletion || 0}%
            </span>
          </div>
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-secondary transition-all duration-300"
              style={{ width: `${play?.glossaryCompletion || 0}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {play?.glossaryTermsAssigned || 0} terms assigned
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 pt-4 border-t border-border">
        <Button
          variant="default"
          size="sm"
          onClick={handleLineEditing}
          iconName="Edit3"
          iconPosition="left"
          fullWidth
        >
          Edit Lines
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/analytics-dashboard', { state: { playId: play?.id } })}
          iconName="BarChart3"
          iconPosition="left"
          fullWidth
        >
          Analytics
        </Button>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <Button
          variant={play?.isPublished ? 'outline' : 'default'}
          size="sm"
          onClick={handleTogglePublish}
          disabled={isTogglingPublish}
          iconName={play?.isPublished ? 'EyeOff' : 'Eye'}
          iconPosition="left"
          fullWidth
        >
          {play?.isPublished ? 'Unpublish' : 'Publish'}
        </Button>
      </div>
      <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
        <span>Uploaded: {new Date(play?.uploadDate)?.toLocaleDateString()}</span>
        <span>Last modified: {new Date(play?.lastModified)?.toLocaleDateString()}</span>
      </div>
    </div>
  );
};

export default PlayListingCard;